class ShopifyDataLayerTracker {
  constructor(config = {}) {
    this.config = {
      debug: false,
      currency: "SEK",
      ...config };


    this.dataLayer = window.dataLayer = window.dataLayer || [];
    this.ctx = window.__SHOPIFY_CONTEXT__ || {};

    this.pageType = this.detectPageType();
    this.currency = this.detectCurrency();

    // Debug state
    this.debugSessionId = `SDL-${Date.now().toString(36)}`;
    this.debugEvents = [];

    this.init();
  }

  /* ---------------------------------------------------------
   * Init
   * --------------------------------------------------------- */
  init() {
    this.debugPageOverview();

    const startTracking = () => {
      if (this.pageType === "product") {
        this.trackViewItem();
      }

      if (this.pageType === "collection") {
        this.trackViewItemList();
        this.bindSelectItem();
      }

      if (this.pageType === "cart") {
        this.trackViewCart();
        this.bindBeginCheckout();
      }

      this.bindAddToCart();
      this.bindQuickAdd();
    };

    if (window.BBCookiebotConsent) {
      window.BBCookiebotConsent.onConsent(["statistics"], startTracking);
    } else if (this.hasAnalyticsConsent()) {
      startTracking();
    }
  }

  /* ---------------------------------------------------------
   * Core emit logic
   * --------------------------------------------------------- */

  emit(eventName, payload = {}) {
    if (!this.hasAnalyticsConsent()) return;

    const finalPayload = {
      event: eventName,
      event_id: this.generateEventId(`${eventName}_`),
      page_type: this.pageType,
      ...payload };


    this.dataLayer.push(finalPayload);

    if (this.config.debug) {var _payload$ecommerce$va, _payload$ecommerce, _payload$ecommerce$it, _payload$ecommerce2, _payload$ecommerce2$i;
      this.debugEvents.push({
        event: eventName,
        value: (_payload$ecommerce$va = payload === null || payload === void 0 ? void 0 : (_payload$ecommerce = payload.ecommerce) === null || _payload$ecommerce === void 0 ? void 0 : _payload$ecommerce.value) !== null && _payload$ecommerce$va !== void 0 ? _payload$ecommerce$va : null,
        items: (_payload$ecommerce$it = payload === null || payload === void 0 ? void 0 : (_payload$ecommerce2 = payload.ecommerce) === null || _payload$ecommerce2 === void 0 ? void 0 : (_payload$ecommerce2$i = _payload$ecommerce2.items) === null || _payload$ecommerce2$i === void 0 ? void 0 : _payload$ecommerce2$i.length) !== null && _payload$ecommerce$it !== void 0 ? _payload$ecommerce$it : 0,
        time: new Date().toLocaleTimeString() });


      console.groupCollapsed(
      `%c[ShopifyDL → dataLayer] ${eventName}`,
      "color:#0284c7;font-weight:600");


      console.log("payload:", finalPayload);
      console.table(this.debugEvents);

      console.groupEnd();
    }
  }

  /* ---------------------------------------------------------
   * GA4 EVENTS
   * --------------------------------------------------------- */

  trackViewItem() {
    const product = this.ctx.product;
    if (!product) return;

    const variant =
    product.variants.find(v => v.id === product.selected_variant_id) ||
    product.variants[0];

    this.emit("view_item", {
      ecommerce: {
        currency: this.currency,
        value: variant.price / 100,
        items: [
        {
          item_id: String(variant.id),
          item_name: product.title,
          item_brand: product.vendor || null,
          item_category: product.type || null,
          item_variant: variant.title,
          price: variant.price / 100,
          quantity: 1 }] } });




  }

  trackViewItemList() {
    const collection = this.ctx.collection;
    if (!collection || !collection.products) return;

    const items = collection.products.map(p => ({
      item_id: String(p.variant.id),
      item_name: p.title,
      item_brand: p.vendor || null,
      item_category: p.type || null,
      item_variant: p.variant.title,
      price: p.variant.price / 100,
      index: p.position }));


    this.emit("view_item_list", {
      ecommerce: {
        currency: this.currency,
        items } });


  }

  trackAddToCart({ product, variant, quantity }) {
    this.emit("add_to_cart", {
      ecommerce: {
        currency: this.currency,
        value: variant.price / 100 * quantity,
        items: [
        {
          item_id: String(variant.id),
          item_name: product.title,
          item_brand: product.vendor || null,
          item_category: product.type || null,
          item_variant: variant.title,
          price: variant.price / 100,
          quantity }] } });




  }

  trackViewCart() {
    const cart = this.ctx.cart;
    if (!cart) return;

    const items = cart.items.map(item => ({
      item_id: String(item.variant_id),
      item_name: item.product_title,
      item_brand: item.vendor || null,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity }));


    this.emit("view_cart", {
      ecommerce: {
        currency: this.currency,
        value: cart.total_price / 100,
        items } });


  }

  trackBeginCheckout(cart) {
    const items = cart.items.map(item => ({
      item_id: String(item.variant_id),
      item_name: item.product_title,
      item_brand: item.vendor || null,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity }));


    this.emit("begin_checkout", {
      ecommerce: {
        currency: this.currency,
        value: cart.total_price / 100,
        items } });


  }

  /* ---------------------------------------------------------
   * Bindings
   * --------------------------------------------------------- */

  bindAddToCart() {
    document.addEventListener("submit", e => {var _form$querySelector, _form$querySelector2;
      const form = e.target;
      if (!form.action.includes("/cart/add")) return;

      this.debugLow("bind:add_to_cart:submit", { action: form.action });

      const product = this.ctx.product;
      if (!product) return;

      const variantId = Number((_form$querySelector = form.querySelector("[name='id']")) === null || _form$querySelector === void 0 ? void 0 : _form$querySelector.value);
      const quantity = Number(((_form$querySelector2 = form.querySelector("[name='quantity']")) === null || _form$querySelector2 === void 0 ? void 0 : _form$querySelector2.value) || 1);
      const variant = product.variants.find(v => v.id === variantId);

      if (variant) {
        this.trackAddToCart({ product, variant, quantity });
      }
    });
  }

  bindSelectItem() {
    document.addEventListener("click", e => {var _link$getAttribute$sp;
      const link = e.target.closest("a[href*='/products/']");
      if (!link || !this.ctx.collection) return;

      this.debugLow("bind:select_item:click", { href: link.href });

      const handle = (_link$getAttribute$sp = link.
      getAttribute("href").
      split("/products/")[1]) === null || _link$getAttribute$sp === void 0 ? void 0 : _link$getAttribute$sp.
      split("?")[0];

      const product = this.ctx.collection.products.find(
      p => p.handle === handle);

      if (!product) return;

      this.emit("select_item", {
        ecommerce: {
          currency: this.currency,
          items: [
          {
            item_id: String(product.variant.id),
            item_name: product.title,
            index: product.position }] } });




    });
  }

  bindBeginCheckout() {
    document.addEventListener("submit", e => {
      const form = e.target;
      if (!form.action.includes("/checkout")) return;

      this.debugLow("bind:begin_checkout:submit", { action: form.action });

      fetch("/cart.js").
      then(r => r.json()).
      then(cart => this.trackBeginCheckout(cart));
    });
  }

  bindQuickAdd() {
    document.addEventListener("click", e => {
      const btn = e.target.closest(".quickadd-variant");
      if (!btn) return;

      this.debugLow("bind:quickadd:click", {
        variant_id: btn.dataset.variantid });


      const variantId = Number(btn.dataset.variantid);
      if (!variantId) return;

      // Look up product + variant from context (collection/index)
      const collection = this.ctx.collection;
      if (!collection || !collection.products) return;

      const product = collection.products.find(p => p.variant.id === variantId);

      if (!product) {
        this.debugLow("bind:quickadd:miss", { variantId });
        return;
      }

      this.trackAddToCart({
        product,
        variant: product.variant,
        quantity: 1 });

    });
  }

  /* ---------------------------------------------------------
   * Consent helpers (Cookiebot)
   * --------------------------------------------------------- */

  hasAnalyticsConsent() {var _window$Cookiebot, _window$Cookiebot$con;
    return !!((_window$Cookiebot = window.Cookiebot) !== null && _window$Cookiebot !== void 0 && (_window$Cookiebot$con = _window$Cookiebot.consent) !== null && _window$Cookiebot$con !== void 0 && _window$Cookiebot$con.statistics);
  }

  hasMarketingConsent() {var _window$Cookiebot2, _window$Cookiebot2$co;
    return !!((_window$Cookiebot2 = window.Cookiebot) !== null && _window$Cookiebot2 !== void 0 && (_window$Cookiebot2$co = _window$Cookiebot2.consent) !== null && _window$Cookiebot2$co !== void 0 && _window$Cookiebot2$co.marketing);
  }

  /* ---------------------------------------------------------
   * Helpers
   * --------------------------------------------------------- */

  generateEventId(prefix = "") {var _crypto$randomUUID, _crypto;
    return prefix + (((_crypto$randomUUID = (_crypto = crypto).randomUUID) === null || _crypto$randomUUID === void 0 ? void 0 : _crypto$randomUUID.call(_crypto)) || `${Date.now()}_${Math.random()}`);
  }

  detectPageType() {
    const p = location.pathname;
    if (/^\/products\//.test(p)) return "product";
    if (/^\/collections\//.test(p)) return "collection";
    if (p.startsWith("/cart")) return "cart";
    if (p === "/") return "index";
    return "unknown";
  }

  detectCurrency() {var _window$Geolizr, _window$Geolizr2;
    return (
      ((_window$Geolizr = window.Geolizr) === null || _window$Geolizr === void 0 ? void 0 : _window$Geolizr.currency) || ((_window$Geolizr2 =
      window.Geolizr) === null || _window$Geolizr2 === void 0 ? void 0 : _window$Geolizr2.activeCurrency) ||
      this.ctx.currency ||
      this.config.currency);

  }

  /* ---------------------------------------------------------
   * Debug utilities
   * --------------------------------------------------------- */

  debugPageOverview() {var _this$ctx$page;
    if (!this.config.debug) return;

    console.group(
    `%c[ShopifyDL] Page Init`,
    "color:#16a34a;font-weight:700;font-size:13px");


    console.table([
    {
      session: this.debugSessionId,
      page_type: this.pageType,
      template: (_this$ctx$page = this.ctx.page) === null || _this$ctx$page === void 0 ? void 0 : _this$ctx$page.template,
      currency: this.currency,
      analytics_consent: this.hasAnalyticsConsent(),
      marketing_consent: this.hasMarketingConsent() }]);



    console.log("Context snapshot:", this.ctx);

    console.groupEnd();
  }

  debugLow(label, payload) {
    if (!this.config.debug) return;
    console.groupCollapsed(
    `%c[ShopifyDL] ${label}`,
    "color:#7c3aed;font-weight:600");

    console.log(payload);
    console.groupEnd();
  }}
